import { Suspense } from 'react'
import { Redirect, Switch } from 'react-router-dom'
import { common, layers } from '@ocean/ui'
import { PrivateRoute } from 'routing/components'
import { PermissionUtils } from 'utils'
import { useLazySelector } from 'hooks'
import { AnimationProvider } from 'animations'
import { InternalLayer, AppLayer, CommunicationObserverLayer } from 'core/layers'
import { SessionLayer } from 'core/session/components'
import { AnimationContainer } from 'components/general'
import AdaptiveLearningContentPlayer from 'modules/AdaptiveLearningContentPlayer'
import contentPlayerRoutes from 'modules/AdaptiveLearningContentPlayer/routes'
import AssessmentPlatform from 'modules/AssessmentPlatform'
import assessmentPlatformRoutes from 'modules/AssessmentPlatform/routes'
import AssessmentPlayer from 'modules/AssessmentPlayer'
import AssessmentPlayerRoutes from 'modules/AssessmentPlayer/routes'
import Dashboard from 'modules/Dashboard'
import dashboardRoutes from 'modules/Dashboard/routes'
import HomeRouting, { routes as homeRoutes } from 'modules/Home/routing'
import NotificationsRouting, { routes as notificationsRoutes } from 'modules/Notifications/routing'
import ProfileRouting, { routes as profileRoutes } from 'modules/Profile/routing'
import SearchRouting, { routes as searchRoutes } from 'modules/Search/routing'
import AproDetailsRouting, {
  routes as aproDetailsRoutes,
} from 'modules/activityDetails/AproDetails/routing'
import CertificateDetailsRouting, {
  routes as certificateDetailsRoutes,
} from 'modules/activityDetails/CertificateDetails/routing'
import CesCstDetailsRouting, {
  routes as cesCstDetailsRoutes,
} from 'modules/activityDetails/CesCstDetails/routing'
import CesDetailedDetailsRouting, {
  routes as cesDetailedDetailsRoutes,
} from 'modules/activityDetails/CesDetailedDetails/routing'
import CesStcwDetailsRouting, {
  routes as cesStcwDetailsRoutes,
} from 'modules/activityDetails/CesStcwDetails/routing'
import CompanyNoticeDetailsRouting, {
  routes as companyNoticeDetailsRoutes,
} from 'modules/activityDetails/CompanyNoticeDetails/routing'
import CompanySpecificTrainingDetailsRouting, {
  routes as companySpecificTrainingDetailsRoutes,
} from 'modules/activityDetails/CompanySpecificTrainingDetails/routing'
import CourseDetailsRouting, {
  routes as courseDetailsRoutes,
} from 'modules/activityDetails/CourseDetails/routing'
import DistanceCourseDetailsRouting, {
  routes as distanceCourseDetailsRoutes,
} from 'modules/activityDetails/DistanceCourseDetails/routing'
import EducationDetailsRouting, {
  routes as educationDetailsRoutes,
} from 'modules/activityDetails/EducationDetails/routing'
import ElearningDetailsRouting, {
  routes as elearningDetailsRoutes,
} from 'modules/activityDetails/ElearningDetails/routing'
import MarlinsEnglishTestDetailsRouting, {
  routes as marlinsEnglishTestDetailsRoutes,
} from 'modules/activityDetails/MarlinsEnglishTestDetails/routing'
import OnboardAssessmentDetailsRouting, {
  routes as onboardAssessmentDetailsRoutes,
} from 'modules/activityDetails/OnboardAssessmentDetails/routing'
import PproDetailsRouting, {
  routes as pproDetailsRoutes,
} from 'modules/activityDetails/PproDetails/routing'
import ProgramDetailsRouting, {
  routes as programDetailsRoutes,
} from 'modules/activityDetails/ProgramDetails/routing'
import QuickGuideDetailsRouting, {
  routes as quickGuideDetailsRoutes,
} from 'modules/activityDetails/QuickGuideDetails/routing'
import RapidElearningDetailsRouting, {
  routes as rapidElearningDetailsRoutes,
} from 'modules/activityDetails/RapidElearningDetails/routing'
import SelfAssessmentDetailsRouting, {
  routes as selfAssessmentDetailsRoutes,
} from 'modules/activityDetails/SelfAssessmentDetails/routing'
import ServiceRecordDetailsRouting, {
  routes as serviceRecordDetailsRoutes,
} from 'modules/activityDetails/ServiceRecordDetails/routing'
import SurveyDetailsRouting, {
  routes as surveyDetailsRoutes,
} from 'modules/activityDetails/SurveyDetails/routing'
import VideoDetailsRouting, {
  routes as videoDetailsRoutes,
} from 'modules/activityDetails/VideoDetails/routing'
import ActivityLogRouting, { activityLogRoutes } from 'modules/activityLog/common/routing'
import ProgramBuilder from 'modules/programBuilder'
import programBuilderRoutes from 'modules/programBuilder/routes'
import RequirementsRouting, {
  routes as requirementsRoutes,
} from 'modules/requirements/common/routing'
import SimulatorTrainingRouting, {
  routes as simulatorTrainingRoutes,
} from 'modules/simulatorTrainings/common/routing'
import DistanceCoursesRouting, {
  routes as distanceCoursesRoutes,
} from 'modules/trainingLibrary/DistanceCourses/routing'
import ElearningRouting, {
  routes as elearningRoutes,
} from 'modules/trainingLibrary/Elearning/routing'
import RapidElearningRouting, {
  routes as rapidElearningRoutes,
} from 'modules/trainingLibrary/RapidElearning/routing'
import SurveysRouting, { routes as surveysRoutes } from 'modules/trainingLibrary/Surveys/routing'
import TrainingLibraryRouting, {
  routes as trainingLibraryRoutes,
} from 'modules/trainingLibrary/TrainingLibrary/routing'
import {
  StudentRouting as VirtualClassroomStudentRouting,
  studentRoutes as virtualClassroomStudentRoutes,
  ManagerRouting as VirtualClassroomManagerRouting,
  managerRoutes as virtualClassroomManagerRoutes,
} from 'modules/virtualClassrooms/common/routing'

const { RouteChangeProgress } = common
const { FullscreenLayer } = layers

const PrivateRouting: React.FC = () => {
  const searchDenied = PermissionUtils.searchDenied()
  const requirementsDenied = PermissionUtils.requirementsDenied()
  const assessmentPlayerDenied = PermissionUtils.assessmentPlayerDenied()
  const adaptiveContentPlayerDenied = PermissionUtils.adaptiveContentPlayerDenied()
  const libraryDenied = PermissionUtils.libraryDenied()
  const cesDenied = PermissionUtils.cesDenied()
  const virtualClassroomDenied = PermissionUtils.virtualClassroomDenied()
  const simulatorTrainingDenied = PermissionUtils.simulatorTrainingDenied()
  const activityLogDenied = PermissionUtils.profileProgressDenied()
  const selfServiceConfigurationDenied = PermissionUtils.selfServiceConfigurationDenied()
  const assessmentPlatformDenied = PermissionUtils.assessmentPlatformDenied()
  const dashboardDenied = PermissionUtils.dashboardDenied()
  const programDenied = PermissionUtils.programDenied()

  const { isLoading, isOffice } = useLazySelector(({ system }) => {
    const { isLoading, result } = system.customerInformation
    return {
      isLoading,
      isOffice: result?.information?.currentInstallation?.isOffice,
    }
  })

  if (isLoading) return <RouteChangeProgress />

  return (
    <SessionLayer>
      <AnimationProvider>
        <FullscreenLayer>
          <AppLayer>
            <CommunicationObserverLayer>
              <Suspense fallback={<RouteChangeProgress />}>
                <AnimationContainer>
                  <Switch>
                    <PrivateRoute path={homeRoutes.root} component={HomeRouting} />
                    <PrivateRoute
                      path={searchRoutes.root}
                      component={SearchRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={activityLogRoutes.root}
                      component={ActivityLogRouting}
                      disabled={activityLogDenied}
                    />
                    <PrivateRoute
                      path={notificationsRoutes.root}
                      component={NotificationsRouting}
                    />
                    <PrivateRoute path={profileRoutes.root} component={ProfileRouting} />
                    <PrivateRoute
                      path={requirementsRoutes.root}
                      component={RequirementsRouting}
                      disabled={requirementsDenied}
                    />
                    <PrivateRoute
                      path={trainingLibraryRoutes.root}
                      component={TrainingLibraryRouting}
                      disabled={libraryDenied}
                    />
                    <PrivateRoute
                      path={distanceCourseDetailsRoutes.root}
                      component={DistanceCourseDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={elearningDetailsRoutes.root}
                      component={ElearningDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={rapidElearningDetailsRoutes.root}
                      component={RapidElearningDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={videoDetailsRoutes.root}
                      component={VideoDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={quickGuideDetailsRoutes.root}
                      component={QuickGuideDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={surveyDetailsRoutes.root}
                      component={SurveyDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={onboardAssessmentDetailsRoutes.root}
                      component={OnboardAssessmentDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={programDetailsRoutes.root}
                      component={ProgramDetailsRouting}
                      disabled={programDenied}
                    />
                    <PrivateRoute
                      path={selfAssessmentDetailsRoutes.root}
                      component={SelfAssessmentDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={companySpecificTrainingDetailsRoutes.root}
                      component={CompanySpecificTrainingDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={certificateDetailsRoutes.root}
                      component={CertificateDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={courseDetailsRoutes.root}
                      component={CourseDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={educationDetailsRoutes.root}
                      component={EducationDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={serviceRecordDetailsRoutes.root}
                      component={ServiceRecordDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={companyNoticeDetailsRoutes.root}
                      component={CompanyNoticeDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={pproDetailsRoutes.root}
                      component={PproDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={cesDetailedDetailsRoutes.root}
                      component={CesDetailedDetailsRouting}
                      disabled={cesDenied}
                    />
                    <PrivateRoute
                      path={cesStcwDetailsRoutes.root}
                      component={CesStcwDetailsRouting}
                      disabled={cesDenied}
                    />
                    <PrivateRoute
                      path={cesCstDetailsRoutes.root}
                      component={CesCstDetailsRouting}
                      disabled={cesDenied}
                    />
                    <PrivateRoute
                      path={marlinsEnglishTestDetailsRoutes.root}
                      component={MarlinsEnglishTestDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={aproDetailsRoutes.root}
                      component={AproDetailsRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={surveysRoutes.root}
                      component={SurveysRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={elearningRoutes.root}
                      component={ElearningRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={rapidElearningRoutes.root}
                      component={RapidElearningRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={distanceCoursesRoutes.root}
                      component={DistanceCoursesRouting}
                      disabled={searchDenied}
                    />
                    <PrivateRoute
                      path={simulatorTrainingRoutes.root}
                      component={SimulatorTrainingRouting}
                      disabled={simulatorTrainingDenied}
                    />
                    <PrivateRoute
                      path={virtualClassroomStudentRoutes.root}
                      component={VirtualClassroomStudentRouting}
                      disabled={virtualClassroomDenied}
                    />
                    <PrivateRoute
                      path={virtualClassroomManagerRoutes.manager}
                      component={VirtualClassroomManagerRouting}
                      disabled={virtualClassroomDenied}
                    />
                    {isOffice ? (
                      <>
                        <PrivateRoute
                          path={programBuilderRoutes.root}
                          component={ProgramBuilder}
                          disabled={selfServiceConfigurationDenied}
                        />
                        <PrivateRoute
                          path={AssessmentPlayerRoutes.root}
                          component={AssessmentPlayer}
                          disabled={assessmentPlayerDenied}
                        />
                        <PrivateRoute
                          path={dashboardRoutes.root}
                          component={Dashboard}
                          disabled={dashboardDenied}
                        />
                        <PrivateRoute
                          path={contentPlayerRoutes.root}
                          component={AdaptiveLearningContentPlayer}
                          disabled={adaptiveContentPlayerDenied}
                        />
                        <PrivateRoute
                          path={assessmentPlatformRoutes.root}
                          component={AssessmentPlatform}
                          disabled={assessmentPlatformDenied}
                        />
                      </>
                    ) : null}
                    <Redirect path='*' to={homeRoutes.root} />
                  </Switch>
                </AnimationContainer>
              </Suspense>
            </CommunicationObserverLayer>
          </AppLayer>
        </FullscreenLayer>
      </AnimationProvider>
    </SessionLayer>
  )
}

const InternalRoutingLayer: React.FC = () => {
  return (
    <InternalLayer>
      <PrivateRouting />
    </InternalLayer>
  )
}

export default InternalRoutingLayer
